import React, { useEffect, useMemo, useState } from 'react';
import { BellRing, Search } from 'lucide-react';
import MaintainerPageShell from '../../components/Base/MaintainerPageShell';
import showToast from '../../utils/toast';
import useNotificationPreferencesService from '../../services/useNotificationPreferencesService';
import './NotificationPreferencesPage.css';

type NotificationType = { value: string; label: string };
type Preference = { enabled: boolean; is_default: boolean };
type PreferenceUser = {
  id: number;
  name: string;
  username: string;
  email: string;
  role: string;
  role_label: string;
  is_superuser: boolean;
  preferences: Record<string, Preference>;
};

const NotificationPreferencesPage: React.FC = () => {
  const { list, update, fetching } = useNotificationPreferencesService();
  const [users, setUsers] = useState<PreferenceUser[]>([]);
  const [notificationTypes, setNotificationTypes] = useState<NotificationType[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let mounted = true;
    list().then((response: any) => {
      if (!mounted || !response?.success) {
        if (response?.message) showToast(response.message, 'error');
        return;
      }
      const data = response.data ?? {};
      setUsers(Array.isArray(data.users) ? data.users : []);
      setNotificationTypes(Array.isArray(data.notification_types) ? data.notification_types : []);
    });
    return () => {
      mounted = false;
    };
  }, [list]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) =>
      [user.name, user.email, user.role_label, user.role, user.username]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term))
    );
  }, [search, users]);

  const togglePreference = async (user: PreferenceUser, notificationType: string) => {
    const current = user.preferences[notificationType];
    if (!current) return;

    const response: any = await update(user.id, {
      [notificationType]: !current.enabled,
    });
    if (!response?.success) {
      showToast(response?.message ?? 'No fue posible actualizar la preferencia.', 'error');
      return;
    }
    const updatedUser = response.data as PreferenceUser;
    setUsers((currentUsers) =>
      currentUsers.map((currentUser) => (currentUser.id === updatedUser.id ? updatedUser : currentUser))
    );
  };

  return (
    <MaintainerPageShell
      title="Preferencias de notificaciones"
      icon={<BellRing size={22} strokeWidth={2.25} />}
      meta={`${users.length} usuarios activos`}
    >
      <div className="notification-preferences__toolbar">
        <label className="notification-preferences__search">
          <Search size={18} aria-hidden />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nombre, correo o rol..."
            aria-label="Buscar usuarios"
          />
        </label>
      </div>

      <div className="notification-preferences__table-wrap">
        <table className="notification-preferences__table">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Rol</th>
              {notificationTypes.map((notificationType) => (
                <th key={notificationType.value}>{notificationType.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td>
                  <strong>{user.name}</strong>
                  <span>{user.email}</span>
                </td>
                <td>{user.is_superuser ? 'Superusuario' : user.role_label}</td>
                {notificationTypes.map((notificationType) => {
                  const preference = user.preferences[notificationType.value];
                  const updating = fetching[`update-${user.id}`];
                  return (
                    <td key={notificationType.value}>
                      <label className="notification-preferences__toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(preference?.enabled)}
                          disabled={updating}
                          onChange={() => togglePreference(user, notificationType.value)}
                          aria-label={`${notificationType.label} para ${user.name}`}
                        />
                        <span aria-hidden />
                      </label>
                      {preference?.is_default && (
                        <small>Predeterminado</small>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {!fetching.list && filteredUsers.length === 0 && (
              <tr>
                <td colSpan={2 + notificationTypes.length} className="notification-preferences__empty">
                  No se encontraron usuarios activos.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </MaintainerPageShell>
  );
};

export default NotificationPreferencesPage;
